import React, { useMemo } from 'react';
import { useUsers } from '../../hooks/useUsers';
import { TrendingUp } from 'lucide-react';

const formatCurrency = (val) => {
  const num = Math.round(Number(val) || 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(num);
};

export default function OpportunitiesOverview({ opportunities = [], onOpportunityClick }) {
  const { users, loading: usersLoading } = useUsers();

  // Filter eligible opportunities: statuses Approved, Proposal, Submitted
  const eligibleStatuses = ['Approved', 'Proposal', 'Submitted'];
  
  const eligibleOpportunities = useMemo(() => {
    return opportunities.filter(opp => eligibleStatuses.includes(opp.status));
  }, [opportunities]);

  // Grand total estimated value
  const grandTotal = useMemo(() => {
    return eligibleOpportunities.reduce((sum, opp) => sum + (Number(opp.estimatedValue) || 0), 0);
  }, [eligibleOpportunities]);

  // Business Development users
  const bdUsers = useMemo(() => {
    return (users || [])
      .filter(u => !u.deleted && u.status !== 'Disabled' && u.roles && (u.roles.includes('Business Development') || u.role === 'Business Development'))
      .map(u => {
        const emailPrefix = u.email ? u.email.split('@')[0].toLowerCase() : '';
        return {
          id: u.id,
          name: u.name || emailPrefix || 'Unnamed User',
          email: u.email || '',
          emailPrefix,
          keys: [u.id?.toLowerCase(), (u.name || '').trim().toLowerCase(), (u.email || '').trim().toLowerCase(), emailPrefix].filter(Boolean)
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  // Helper to match opportunity to a user
  const isOppAssignedToUser = (opp, userObj) => {
    const rawAssigned = (opp.assignedTo || opp.owner || opp.createdBy || opp.assignedUser || '').trim().toLowerCase();
    if (!rawAssigned) return false;
    return userObj.keys.includes(rawAssigned);
  };

  // User breakdown statistics: just name and total
  const userBreakdown = useMemo(() => {
    return bdUsers.map(user => {
      const userOpps = eligibleOpportunities.filter(opp => isOppAssignedToUser(opp, user));
      const subtotal = userOpps.reduce((sum, opp) => sum + (Number(opp.estimatedValue) || 0), 0);
      return {
        id: user.id,
        name: user.name,
        total: subtotal
      };
    });
  }, [bdUsers, eligibleOpportunities]);

  // Unassigned opportunities total
  const unassignedTotal = useMemo(() => {
    const unassignedOpps = eligibleOpportunities.filter(opp => {
      const rawAssigned = (opp.assignedTo || opp.owner || opp.createdBy || opp.assignedUser || '').trim().toLowerCase();
      if (!rawAssigned) return true;
      return !bdUsers.some(u => u.keys.includes(rawAssigned));
    });
    return unassignedOpps.reduce((sum, opp) => sum + (Number(opp.estimatedValue) || 0), 0);
  }, [eligibleOpportunities, bdUsers]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '650px', width: '100%', paddingRight: '0.25rem' }}>
      
      {/* Total Opportunities Card with BD Breakdown */}
      <div className="card stat-card" style={{ padding: '1.5rem', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        
        {/* Header / Grand Total */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
          <div>
            <span className="stat-label" style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Total Opportunities
            </span>
            <div className="stat-value" style={{ fontSize: '2.4rem', fontWeight: 700, margin: '0.35rem 0' }}>
              {formatCurrency(grandTotal)}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Statuses: <span style={{ color: 'var(--primary)' }}>Approved, Proposal, Submitted</span>
            </div>
          </div>
          <div 
            className="stat-icon-wrapper"
            style={{ 
              backgroundColor: 'rgba(0, 224, 150, 0.12)', 
              color: 'var(--primary)',
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <TrendingUp size={28} />
          </div>
        </div>

        {/* Small List: BD Subtotal Breakdown */}
        <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', width: '100%' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>
            BD Breakdown
          </div>

          {bdUsers.length === 0 ? (
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              No Business Development users found.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {userBreakdown.map((user) => (
                <div 
                  key={user.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.4rem 0.6rem',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(255, 255, 255, 0.02)'
                  }}
                >
                  <span style={{ fontSize: '0.9rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                    {user.name}
                  </span>
                  <span style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--primary)' }}>
                    {formatCurrency(user.total)}
                  </span>
                </div>
              ))}

              {unassignedTotal > 0 && (
                <div 
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.4rem 0.6rem',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(255, 255, 255, 0.01)',
                    borderTop: '1px dashed var(--border-color)'
                  }}
                >
                  <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                    Unassigned
                  </span>
                  <span style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                    {formatCurrency(unassignedTotal)}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

      </div>

    </div>
  );
}
